'use client'

import { createStore, type StoreApi } from 'zustand/vanilla'
import { useStore } from 'zustand'
import { createContext, useContext, useRef, type ReactNode } from 'react'
import type { Team, Project, SideNavState } from '@/lib/mock-data'
import { persistLastActiveTeam } from '@/stores/preferences-store'
import { getCurrentTenantId } from '@/lib/jwt'

const SIDENAV_STORAGE_KEY = 'obzoo-sidenav-state'

// sameTeamValue compares two teams by VALUE, not identity.
//
// Deliberately a field-by-field compare rather than a reference check: the
// callers that matter re-derive the object on every render, so references
// never match and a reference check would guard nothing.
export function sameTeamValue(a: Team | null, b: Team | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  const ka = Object.keys(a) as (keyof Team)[]
  const kb = Object.keys(b) as (keyof Team)[]
  if (ka.length !== kb.length) return false
  return ka.every((k) => a[k] === b[k])
}

export interface LayoutState {
  // --- Side nav ---
  sideNavState: SideNavState
  sideNavStateBeforeWorkspace: SideNavState | null
  setSideNavState: (state: SideNavState) => void
  cycleSideNav: () => void
  toggleSideNavCollapseHidden: () => void

  // --- Preserved ---
  currentTeam: Team | null
  currentProject: Project | null
  isWorkspaceMode: boolean
  sidebarCollapsed: boolean
  bottomPanelExpanded: boolean

  // --- Presentation mode (immersive doc reading) ---
  // True → AppLayout hides TopBar+SideNav, EditorPanel hides tabs/outline/AI
  // chrome, content centers within a narrow column, BlockNote becomes read-only.
  // Auto-exit when the active doc changes (per-session, not persisted).
  presentationMode: boolean
  setPresentationMode: (enabled: boolean) => void

  // --- Preserved methods ---
  setCurrentTeam: (team: Team | null) => void
  updateCurrentTeamName: (name: string) => void
  setCurrentProject: (project: Project | null) => void
  setWorkspaceMode: (enabled: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebar: () => void
  setBottomPanelExpanded: (expanded: boolean) => void
  toggleBottomPanel: () => void
}

export const createLayoutStore = () =>
  createStore<LayoutState>((set, get) => ({
    sideNavState: 'expanded',
    sideNavStateBeforeWorkspace: null,
    currentTeam: null,
    currentProject: null,
    isWorkspaceMode: false,
    sidebarCollapsed: false,
    bottomPanelExpanded: false,
    presentationMode: false,
    setPresentationMode: (enabled) => set({ presentationMode: enabled }),

    setSideNavState: (state) => {
      set({ sideNavState: state })
      if (!get().isWorkspaceMode && typeof window !== 'undefined') {
        localStorage.setItem(SIDENAV_STORAGE_KEY, state)
      }
    },

    cycleSideNav: () => {
      const { sideNavState, isWorkspaceMode } = get()
      if (isWorkspaceMode) {
        // Workspace: only collapsed <-> hidden
        const next: SideNavState = sideNavState === 'collapsed' ? 'hidden' : 'collapsed'
        set({ sideNavState: next })
      } else {
        // Normal: expanded -> collapsed -> hidden -> collapsed (cycle)
        let next: SideNavState
        if (sideNavState === 'expanded') {
          next = 'collapsed'
        } else if (sideNavState === 'collapsed') {
          next = 'hidden'
        } else {
          next = 'collapsed'
        }
        set({ sideNavState: next })
        if (typeof window !== 'undefined') {
          localStorage.setItem(SIDENAV_STORAGE_KEY, next)
        }
      }
    },

    toggleSideNavCollapseHidden: () => {
      const { sideNavState } = get()
      const next: SideNavState = sideNavState === 'collapsed' ? 'hidden' : 'collapsed'
      set({ sideNavState: next })
    },

    setCurrentTeam: (team) => {
      const prev = get().currentTeam
      // Identity guard. TeamLayout derives its team with `teams.find(...)`
      // and pushes it here from an effect keyed on that object, so every
      // refresh of the team list hands us a BRAND NEW object describing the
      // very same team. Writing it through re-rendered every currentTeam
      // subscriber across the app for no change at all — an amplifier that
      // turns one store refresh into a render pass over everything.
      //
      // Under a burst of refreshes (two BYO workers upgrading at once is the
      // case that surfaced it) that feedback is what React reports as
      // "Maximum update depth exceeded" (#185). Comparing by value stops it
      // at the source, wherever the redundant call comes from.
      if (sameTeamValue(prev, team)) return
      set({ currentTeam: team })
      // Persist last-active team server-side, bucketed by the CURRENT tenant,
      // so the next cold load / return to this tenant defaults back to it.
      // Fire-and-forget — failures don't break the local switch, and the
      // value is read by StoreInitializer via the preferences store next
      // session. Skip when the id hasn't changed to avoid pointless writes
      // (e.g. URL-driven setCurrentTeam for the same team on every route
      // change inside /t/[teamId]). persistLastActiveTeam also no-ops when
      // the cached value already matches, and always writes back the FULL
      // per-tenant map so other tenants' remembered teams survive.
      if (team && team.id !== prev?.id) {
        persistLastActiveTeam(getCurrentTenantId(), team.id)
      }
    },
    updateCurrentTeamName: (name) =>
      set((s) => s.currentTeam ? { currentTeam: { ...s.currentTeam, name } } : {}),
    setCurrentProject: (project) => set({ currentProject: project }),
    setWorkspaceMode: (enabled) => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
      if (enabled) {
        // Entering workspace: save current sidenav state (hidden on mobile, collapsed on desktop)
        set({
          isWorkspaceMode: true,
          sideNavStateBeforeWorkspace: get().sideNavState,
          sideNavState: isMobile ? 'hidden' : 'collapsed',
        })
      } else {
        // Leaving workspace: restore previous sidenav state (hidden on mobile).
        // DESKTOP MUST NEVER LAND ON 'hidden' here: the page we're leaving TO
        // (team settings, kb, etc.) is a non-workspace surface with no in-canvas
        // reopen affordance, so a hidden sidebar strands the user with no way to
        // switch pages. If the pre-workspace state was 'hidden' (or unknown),
        // fall back to 'expanded' — matching a fresh page load, which also shows
        // the sidebar. A prior 'collapsed'/'expanded' is preserved (both visible).
        // Mobile keeps 'hidden' (the TopBar hamburger reopens it there).
        const before = get().sideNavStateBeforeWorkspace
        const restored: SideNavState = isMobile
          ? 'hidden'
          : before && before !== 'hidden'
            ? before
            : 'expanded'
        set({
          isWorkspaceMode: false,
          sideNavStateBeforeWorkspace: null,
          sideNavState: restored,
        })
      }
    },
    setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
    toggleSidebar: () =>
      set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
    setBottomPanelExpanded: (expanded) => set({ bottomPanelExpanded: expanded }),
    toggleBottomPanel: () =>
      set((state) => ({ bottomPanelExpanded: !state.bottomPanelExpanded })),
  }))

// Helper selectors
export const selectIsSideNavExpanded = (s: LayoutState) => s.sideNavState === 'expanded'
export const selectIsSideNavCollapsed = (s: LayoutState) => s.sideNavState === 'collapsed'
export const selectIsSideNavHidden = (s: LayoutState) => s.sideNavState === 'hidden'

// Context
const LayoutStoreContext = createContext<StoreApi<LayoutState> | null>(null)

// Provider
export function LayoutStoreProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<StoreApi<LayoutState>>(null)
  if (!storeRef.current) {
    storeRef.current = createLayoutStore()
  }
  return (
    <LayoutStoreContext value={storeRef.current}>
      {children}
    </LayoutStoreContext>
  )
}

// Hook
export function useLayoutStore<T>(
  selector: (state: LayoutState) => T,
): T {
  const store = useContext(LayoutStoreContext)
  if (!store) throw new Error('Missing LayoutStoreProvider')
  return useStore(store, selector)
}

// Imperative store access — for use OUTSIDE React render (event handlers,
// timers). Returns the raw zustand store API so callers can read state or
// dispatch actions without subscribing.
export function useLayoutStoreApi(): StoreApi<LayoutState> {
  const store = useContext(LayoutStoreContext)
  if (!store) throw new Error('Missing LayoutStoreProvider')
  return store
}
